import { Checkbox, Space, Tag, Text, WithTooltip } from '@procurify/ui'

import {
  configureColumn as col,
  DataTable,
  type RowCellMenuListItemProps,
} from '@webapp/components/DataTable'
import { BridgeLink } from '@webapp/core/components/BridgeLink'
import { FormattedShortDate } from '@webapp/core/components/FormattedDate'
import { PaginationControls } from '@webapp/core/components/PaginationControls'
import { Price } from '@webapp/core/components/Price'
import { useProcurifyIntl } from '@webapp/hooks'
import { type CreditCardItem } from '@webapp/modules/accounts-payable/entities/CreditCardItem'
import messages from './messages'

export interface ICreditCardItemTableProps {
  items: CreditCardItem[]
  isLoading: boolean
  /** Move-mode: renders row checkboxes + a header select-all (BHV-021) */
  selectionMode: boolean
  selectedItemIds: number[]
  onSelectionChange: (ids: number[]) => void
  /** FS slot composing sibling command features into the row menu (ADR-9) */
  renderRowMenu: (item: CreditCardItem) => RowCellMenuListItemProps[]
}

const getRefUrl = (item: CreditCardItem): Nullable<string> => {
  if (item.po) return `/#/orders/${item.po.uuid}`
  if (item.expense) return `/#/status/expense-report/${item.expense.uuid}`
  return null
}

export const CreditCardItemTable = ({
  items,
  isLoading,
  selectionMode,
  selectedItemIds,
  onSelectionChange,
  renderRowMenu,
}: ICreditCardItemTableProps) => {
  const intl = useProcurifyIntl()

  const movableItems = items.filter((item) => item.isMovable)
  const allMovableSelected =
    movableItems.length > 0 &&
    movableItems.every((item) => selectedItemIds.includes(item.id))

  const handleToggleSelect = (item: CreditCardItem) => {
    onSelectionChange(
      selectedItemIds.includes(item.id)
        ? selectedItemIds.filter((id) => id !== item.id)
        : [...selectedItemIds, item.id]
    )
  }

  // Header select-all targets every movable row (BHV-022).
  const selectionColumn = col('BASIC', {
    label: '',
    headerCheckbox: true,
    headerChecked: allMovableSelected,
    onHeaderCheck: (checked: boolean) =>
      onSelectionChange(checked ? movableItems.map((item) => item.id) : []),
    maxWidth: 48,
    getter: (item: CreditCardItem) => (
      <WithTooltip
        title={intl.formatMessage(messages.unableToMoveTooltip)}
        isDisabled={item.isMovable}
      >
        <WithTooltip.Wrapper isContentDisabled={!item.isMovable}>
          <Checkbox
            checked={selectedItemIds.includes(item.id)}
            disabled={!item.isMovable}
            onChange={() => handleToggleSelect(item)}
            inputProps={{
              'aria-label': intl.formatMessage(messages.selectItemAriaLabel, {
                description: item.descriptionLabel,
              }),
            }}
          />
        </WithTooltip.Wrapper>
      </WithTooltip>
    ),
  })

  const columns = [
    ...(selectionMode ? [selectionColumn] : []),
    col('BASIC', {
      label: intl.formatMessage(messages.refColumnLabel),
      maxWidth: 160,
      getter: (item: CreditCardItem) => {
        const refUrl = getRefUrl(item)

        return item.refLabel && refUrl ? (
          <Space direction='horizontal' size='small' align='center'>
            <Tag>
              {item.po
                ? intl.formatMessage(messages.poTag)
                : intl.formatMessage(messages.rfeTag)}
            </Tag>
            <BridgeLink to={refUrl}>
              {item.po?.number ?? String(item.expense?.expenseReportId ?? '')}
            </BridgeLink>
          </Space>
        ) : (
          '-'
        )
      },
    }),
    col('BASIC', {
      label: intl.formatMessage(messages.dateColumnLabel),
      maxWidth: 140,
      getter: (item: CreditCardItem) => (
        <FormattedShortDate date={item.receivedOn} />
      ),
    }),
    col('BASIC', {
      label: intl.formatMessage(messages.descriptionColumnLabel),
      getter: (item: CreditCardItem) => item.descriptionLabel,
    }),
    col('BASIC', {
      label: intl.formatMessage(messages.accountCodeColumnLabel),
      getter: (item: CreditCardItem) => item.accountCodeLabel,
    }),
    col('BASIC', {
      label: intl.formatMessage(messages.totalCostColumnLabel),
      maxWidth: 160,
      getter: (item: CreditCardItem) => (
        <Text bold autoMargin={false}>
          <Price price={item.totalCost} currency={item.currency.name} inline />
        </Text>
      ),
    }),
  ]

  return (
    <PaginationControls id='ap-reconcile-items'>
      <Space size='large'>
        <DataTable<CreditCardItem>
          id='credit-card-item-table'
          aria-label={intl.formatMessage(messages.tableAriaLabel)}
          data={items}
          columns={columns}
          isLoading={isLoading}
          customizableColumns={false}
          persistCustomizedColumns={false}
          emptyStateText={intl.formatMessage(messages.emptyStateText)}
          emptyStateSize='small'
          menuListItems={selectionMode ? undefined : renderRowMenu}
          hoverable
        />

        <Space direction='horizontal' justify='space-between'>
          <PaginationControls.PageLimit />
          <PaginationControls.Page />
        </Space>
      </Space>
    </PaginationControls>
  )
}

CreditCardItemTable.displayName = 'CreditCardItemTable'
